/**
 * "Documents in this chat's context" for the inbox chat modal (FRONT-357).
 *
 * Files uploaded into the modal's conversation stay in context: the modal
 * re-sends them with every turn (as `contextAttachments`, injected
 * server-side like normal attachments but never persisted) until the user
 * removes their chip. The list itself is DERIVED from the conversation
 * history — every user message carries its attachments in metadata — minus
 * the file ids the user has removed. Removals are remembered per session in
 * localStorage so a reopened modal doesn't resurrect a removed file.
 *
 * Pure module (plus the two thin localStorage wrappers) — exported for unit
 * tests, following the document-edit.ts / modal-asset.ts pattern.
 */

import type { UIMessage } from 'ai';
import type { ChatAttachment } from './chat-input';

const REMOVED_LS_PREFIX = 'eddy.inboxChat.removedContextFiles.';

/**
 * Collect the unique files uploaded in this conversation, in order of first
 * appearance, skipping the ones the user removed from context. Tolerant of
 * anything malformed in metadata — history rows and optimistic messages
 * both pass through here.
 */
export function deriveContextFiles(
  messages: Array<Pick<UIMessage, 'role' | 'metadata'>>,
  removedFileIds: ReadonlySet<string>,
): ChatAttachment[] {
  const seen = new Set<string>();
  const files: ChatAttachment[] = [];
  for (const msg of messages) {
    if (msg.role !== 'user') continue;
    const atts = (msg.metadata as { attachments?: unknown } | undefined)?.attachments;
    if (!Array.isArray(atts)) continue;
    for (const att of atts) {
      const a = att as Partial<ChatAttachment> | null;
      if (!a || typeof a.fileId !== 'string' || typeof a.name !== 'string') continue;
      // FRONT-1567: a picked document's `fileId` is a filename, not an
      // upload id — losing `source` here would resolve it as a (likely
      // nonexistent) upload on every later turn. Absent (every message
      // sent before this ticket) still normalises to 'upload'.
      const source = a.source === 'document' ? 'document' : 'upload';
      // Dedup on source+fileId — matching `mergeAttachmentRefs`'s backend
      // key (chat-attachments.ts) — so a document filename can't collide
      // with a same-string upload id. `removedFileIds` stays keyed on the
      // BARE fileId on purpose: it's persisted in localStorage per session
      // (see `loadRemovedFileIds`/`persistRemovedFileIds` below), and
      // re-keying it to source:fileId would silently resurrect every chip
      // a user had already dismissed under the old bare-id scheme. Do not
      // "fix" the two keys back to matching — they're deliberately not.
      const key = `${source}:${a.fileId}`;
      if (seen.has(key) || removedFileIds.has(a.fileId)) continue;
      seen.add(key);
      files.push({
        fileId: a.fileId,
        name: a.name,
        contentType: typeof a.contentType === 'string' ? a.contentType : '',
        kind: typeof a.kind === 'string' ? a.kind : 'binary',
        source,
      });
    }
  }
  return files;
}

/** Load the per-session set of removed file ids. Best-effort. */
export function loadRemovedFileIds(sessionId: string): Set<string> {
  try {
    const raw = localStorage.getItem(REMOVED_LS_PREFIX + sessionId);
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    return new Set(
      Array.isArray(parsed) ? parsed.filter((x): x is string => typeof x === 'string') : [],
    );
  } catch {
    return new Set();
  }
}

/** Persist the per-session set of removed file ids. Best-effort. */
export function persistRemovedFileIds(sessionId: string, ids: ReadonlySet<string>): void {
  try {
    localStorage.setItem(REMOVED_LS_PREFIX + sessionId, JSON.stringify([...ids]));
  } catch {
    // Storage full / unavailable — removals then last for this mount only.
  }
}
